import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE } from "@/lib/api";
import { Plus, Eye, PencilSimple, Trash, Copy, Link as LinkIcon, ChartLine } from "@phosphor-icons/react";

const PUBLIC_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

export default function SplashPages() {
  const [pages, setPages] = useState([]);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/splash/pages");
      setPages(data.pages || []);
      setLimit(data.limit || 25);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyLink = async (slug) => {
    const url = `${PUBLIC_ORIGIN}/s/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied!");
    } catch {
      window.prompt("Copy this URL:", url);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? Its public URL will stop working immediately.`)) return;
    try {
      await api.delete(`/splash/pages/${p.id}`);
      toast.success("Splash page deleted");
      setPages((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 data-testid="splash-heading" className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              Splash pages
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
              Build simple landing pages with your own headlines, images and links. Each page gets a unique short URL you can share anywhere.
            </p>
          </div>
          <button
            data-testid="splash-new-btn"
            onClick={() => nav("/splash/new")}
            disabled={pages.length >= limit}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-4 pr-1.5 py-1.5 text-xs font-black tracking-[0.2em] shadow-[0_6px_0_rgba(163,230,53,1)] hover:shadow-[0_3px_0_rgba(163,230,53,1)] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none"
          >
            CREATE SPLASH PAGE
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-lime-400 text-slate-900">
              <Plus size={14} weight="bold" />
            </span>
          </button>
        </div>

        {loading ? (
          <div className="text-slate-400">Loading…</div>
        ) : pages.length === 0 ? (
          <EmptyState onCreate={() => nav("/splash/new")} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pages.map((p) => (
              <PageCard
                key={p.id}
                page={p}
                publicUrl={`${PUBLIC_ORIGIN}/s/${p.slug}`}
                onCopy={() => copyLink(p.slug)}
                onEdit={() => nav(`/splash/${p.id}/edit`)}
                onView={() => window.open(`/s/${p.slug}`, "_blank", "noopener,noreferrer")}
                onDelete={() => remove(p)}
              />
            ))}
          </div>
        )}

        <div className="mt-6 text-xs text-slate-400">
          {pages.length} of {limit} splash pages used.
        </div>
      </div>
    </DashboardLayout>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-12 text-center bg-white dark:bg-slate-900">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-lime-400 to-emerald-500 text-slate-900 mb-4">
        <ChartLine size={26} weight="bold" />
      </div>
      <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
        No splash pages yet
      </h2>
      <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
        Create your first landing page in under a minute. Add a headline, an image, a call-to-action, and share the link anywhere.
      </p>
      <button
        data-testid="splash-empty-create-btn"
        onClick={onCreate}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-xs font-black tracking-[0.2em] hover:bg-slate-800 transition-colors"
      >
        <Plus size={14} weight="bold" /> Create your first splash page
      </button>
    </div>
  );
}

function PageCard({ page, publicUrl, onCopy, onEdit, onView, onDelete }) {
  const created = new Date(page.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return (
    <div data-testid={`splash-card-${page.slug}`} className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
      <div
        className="h-16 relative flex items-center justify-center"
        style={{ background: page.background || "#0ea5e9" }}
      >
        {/* Subtle contrast overlay so the badge is always readable regardless
            of the background color the user picked. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-black/20" />
        <div
          className="relative text-xs font-black uppercase tracking-widest px-4 truncate"
          style={{ color: page.text_color || "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
        >
          Preview
        </div>
      </div>
      <div className="p-4 space-y-3">
        {/* Full title always shown here in the card body — guaranteed readable */}
        <div data-testid={`splash-title-${page.slug}`} className="text-base font-black tracking-tight text-slate-900 dark:text-white leading-tight line-clamp-2">
          {page.title}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <LinkIcon size={12} weight="bold" className="text-blue-500 shrink-0" />
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-blue-600 dark:text-blue-400 hover:underline truncate"
          >
            /s/{page.slug}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Views</div>
            <div data-testid={`splash-views-${page.slug}`} className="text-lg font-black text-slate-900 dark:text-white">
              {page.views ?? 0}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Clicks</div>
            <div data-testid={`splash-clicks-${page.slug}`} className="text-lg font-black text-slate-900 dark:text-white">
              {page.clicks ?? 0}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-400">Created {created}</div>

        <div className="grid grid-cols-4 gap-1.5">
          <IconAction testid={`splash-view-${page.slug}`} icon={Eye} label="View" onClick={onView} />
          <IconAction testid={`splash-edit-${page.slug}`} icon={PencilSimple} label="Edit" onClick={onEdit} />
          <IconAction testid={`splash-copy-${page.slug}`} icon={Copy} label="Copy" onClick={onCopy} />
          <IconAction testid={`splash-delete-${page.slug}`} icon={Trash} label="Delete" onClick={onDelete} danger />
        </div>
      </div>
    </div>
  );
}

function IconAction({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex flex-col items-center gap-0.5 rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${danger ? "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
    >
      <Icon size={16} weight="bold" />
      {label}
    </button>
  );
}
